<template>
  <g-button class="mx-2" label="Export" icon="get_app" :loading="loading" @onclick="exportData" />
</template>

<script>
import { ExportService } from "@/modules/exports";
import { ProductService } from "@/modules/products";

export default {
  props: {
    searchQuery: Object,
    dbSource: String,
    focusYear: String,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    appliedSeasonRate() {
      return this.$store.getters["DatabaseStore/productPriceDate"];
    },
    env() {
      return this.$store.getters["AccountStore/environment"];
    },
  },
  methods: {
    exportData() {
      this.loading = true;

      return this.$store
        .dispatch("DatabaseStore/queryProducts", {
          type: this.dbSource,
          query: this.searchQuery,
          page: 0,
        })
        .then((rows) => {
          const fileName = `products-${this.dbSource}-${new Date().getTime()}.xlsx`;
          const exportRows = [
            [],
            [],
            [
              "Supplier name",
              "City",
              "Product name",
              "Price",
              "Rate year",
              "Rate status",
              "Rate type",
              "Rate kind",
              "Breakfast included",
              "Meal style",
            ],
            ...(rows || []).map((product) => {
              const focusRate = this.getFocusRate(product);
              return [
                this.clean(this.getSupplierName(product)),
                this.clean(this.getCity(product)),
                this.clean(product.name),
                this.clean(this.getPrice(product, focusRate)),
                this.clean(this.getRateYear(focusRate)),
                this.clean(this.getRateStatus(focusRate)),
                this.clean(this.getRateType(focusRate)),
                this.clean(this.getRateKind(focusRate)),
                this.clean(this.getBreakfastIncluded(product)),
                this.clean(this.getMealStyle(product)),
              ];
            }),
          ];

          return ExportService({
            fileName,
            rows: exportRows,
            iconArea: "A1:B1",
            firstRowHeight: 90,
            landscape: true,
            printArea: `A1:J${exportRows.length}`,
            config: this.exportConfig(exportRows.length),
          });
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportConfig(rowCount) {
      const headerFill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE8F3DC" },
      };
      const border = {
        top: { style: "thin", color: { argb: "FFB7B7B7" } },
        left: { style: "thin", color: { argb: "FFB7B7B7" } },
        bottom: { style: "thin", color: { argb: "FFB7B7B7" } },
        right: { style: "thin", color: { argb: "FFB7B7B7" } },
      };

      const config = [
        { row: 0, column: 1, options: { width: 32 } },
        { row: 0, column: 2, options: { width: 22 } },
        { row: 0, column: 3, options: { width: 36 } },
        { row: 0, column: 4, options: { width: 24 } },
        { row: 0, column: 5, options: { width: 14 } },
        { row: 0, column: 6, options: { width: 18 } },
        { row: 0, column: 7, options: { width: 14 } },
        { row: 0, column: 8, options: { width: 16 } },
        { row: 0, column: 9, options: { width: 20 } },
        { row: 0, column: 10, options: { width: 18 } },
        {
          row: 3,
          options: {
            font: { bold: true },
            fill: headerFill,
            border,
            alignment: { vertical: "middle" },
            height: 24,
          },
        },
      ];

      for (let row = 4; row <= rowCount; row++) {
        config.push({
          row,
          options: {
            border,
            alignment: { vertical: "top", wrapText: true },
          },
        });
      }

      return config;
    },
    getSupplierName(product) {
      if (product.supplier_meta && product.supplier_meta.en_name) {
        return `${product.supplier_meta.en_name} (${product.supplier_name})`;
      }
      return product.supplier_name || "";
    },
    getCity(product) {
      if (product.subcity) return `${product.city} (${product.subcity})`;
      return product.city || "";
    },
    getPrice(product, focusRate = this.getFocusRate(product)) {
      const prices = this.getPriceValues(product, focusRate);

      if (!prices) return "";
      if (!prices.length && focusRate.seasonal) return "(Seasonal)";
      if (prices && !focusRate.seasonal) return ProductService.formatPrice(prices);

      return prices
        .map((p) => `${ProductService.formatPrice(p)} (${p.dateStart} to ${p.dateEnd})`)
        .join("; ");
    },
    getFocusRate(product) {
      if (!product.rate_history || !product.rate_history.length) return { rate: {}, meta: {} };

      let behind = false;
      let selectedYearRate = product.rate_history.find((item) => item.rate_year === this.focusYear);

      if (!selectedYearRate) {
        behind = true;
        selectedYearRate = product.rate_history[0];
      }

      return {
        usingOldDate: behind,
        ...(selectedYearRate || {}),
        supplier_meta: product.supplier_meta,
        country: product.country,
        product_type: product.product_type,
      };
    },
    getPriceValues(product, focusRate) {
      if (!focusRate.rate) return "";

      const meta = focusRate.supplier_meta || product.supplier_meta || {};
      const country = focusRate.country || product.country;

      if (!focusRate.seasonal) {
        return ProductService.fullCalc(focusRate, meta, country, {});
      }

      const pricesInRange = ProductService.getSeasonalRange(focusRate.rate || [], this.appliedSeasonRate);

      return pricesInRange.map((prod) => {
        const temp = { ...focusRate, rate: prod.rate };
        return {
          ...ProductService.fullCalc(temp, meta, country, {}),
          dateStart: ProductService.getDate(prod.mstart, prod.dstart),
          dateEnd: ProductService.getDate(prod.mend, prod.dend),
        };
      });
    },
    getRateYear(focusRate) {
      return focusRate.rate_year || "";
    },
    getRateStatus(focusRate) {
      if (!focusRate.rate_year) return "";
      if (focusRate.usingOldDate) return "Confirmed";
      return focusRate.confirmed ? "Confirmed" : "Unconfirmed";
    },
    getRateType(focusRate) {
      if (!focusRate.rate_year) return "";
      return focusRate.seasonal ? "Seasonal" : "Normal";
    },
    getRateKind(focusRate) {
      const rateKind = focusRate.meta && focusRate.meta.rate_kind;
      if (rateKind === "CONTRACT") return "Contracted";
      if (rateKind === "GENERIC") return "Generic";
      return "";
    },
    getBreakfastIncluded(product) {
      if (product.product_type !== "ROOM") return "";

      const supplierMeta = product.supplier_meta || {};
      const breakfast = supplierMeta.breakfast;

      if (!breakfast || breakfast.value !== "included") return "No";

      return breakfast.text ? `Yes (${breakfast.text})` : "Yes";
    },
    getMealStyle(product) {
      if (this.dbSource !== "MEAL") return "";
      return (product.supplier_meta && product.supplier_meta.style) || "";
    },
    clean(value) {
      if (!value) return "";
      return `${value}`.replace(/<[^>]*>/g, "").trim();
    },
  },
};
</script>
